/**
 * System Roles:
 *
 * Admin = Can add/manage users, manage entities, create/edit round. CANNOT perform any round tasks unless given a round role (they can give themselves controller, or a controller can give them owner or assignee role)
 * Data input = Not an admin. Can access round if associated with entity.
 *
 * Round Roles:
 *
 * Controller = can do every thing owner can do + start round, edit round, edit stages, view organisation sources, move organisation sources to active, manage active sources (archive).  CANNOT add/manage users, manage entities, create/edit round
 * Owner = can do everything assignee can do + approve question, manage assignee (for questions they are owner of)
 * Assignee = can do everything viewer can do + edit answer, apply previous draft, apply AI draft, associate source, mark for question for approval, change status (for questions they are assignee of)
 * Viewer = can view question, run AI review, use knowledge base, use AI assist, save AI draft (NOT apply), run report, view dashboard, view active sources, add sources
 *
 * Warning: These methods may have caching issues. In practice that will rarely come up.
 * TODO: clearer use of cached data, and prefer useQuery() over DIY caching for managing refreshing data.
 */

import { useMemo } from "react";
import { useAuth } from "../context";
import { useSelector } from "react-redux";
import { useGetEntitiesForUser } from "hooks/query/issb/issb";
import { equalsIgnoreCase, includesIgnoreCase } from "./text-utils";
import GLOBAL from "context/global";
import assert from "utils/assert";

// Helper functions
const checkIsOrgAdmin = (permissions) => permissions?.includes("ORG_ADMIN"); // done

/**
 * @returns true for user.email in round.ownerEmails or for OrgAdmin who can do everything within an organisation
 * 
// Note: future corner-case bug: since rounds are cached in the browser's localStorage
// controller edits won't propagate to other users
 */
const checkIsController = (round, user, userPermissions) => {
  if (!round || !user) {
    return false;
  }
  if (checkIsOrgAdmin(userPermissions)) {
    return true;
  }
  return (round.ownerEmails ?? []).some((email) => equalsIgnoreCase(email, user.email));
};

/** TODO this has a caching issue - the question object may be out of date */
const checkIsOwner = (question, user) => {
  return equalsIgnoreCase(question?.owner, user?.email);
};

/** TODO this has a caching issue - the question object may be out of date */
const checkIsAssigned = (question, user) => {
  return includesIgnoreCase(question?.assignees, user?.email);
};

const checkIsAuditor = (question, activeAudits, user) => {
  /*
   1. check if question is part of any active audits
   2. if yes, check if user is auditor in that audit
   */
  if (!question || !activeAudits || !user) {
    return false;
  }
  const audit = activeAudits?.find((audit) => audit?.questionIds?.includes(question.id));
  if (audit) {
    return includesIgnoreCase(audit?.auditors, user?.email);
  }
  return false;
};

// custom hook
// (Dan: @Ye-Lynn -- nice work!)
const useAirUserRoles = () => {
  const { userPermissions = [], user } = useAuth();

  // Round is set by: RoundList
  // activeAudits and questions are set via useSyncActiveAuditsWthSelectedRound and useSyncQuestionsWithSelectedRound
  const { selectedRound, questions, audits } = useSelector((state) => state.issbRound || {});

  const { data: userEntities } = useGetEntitiesForUser({
    enabled: !!user,
  });

  const {
    isOrgAdmin,
    isESGAdmin,
    isController,
    isDeveloper,
    isOwner,
    isAssigned,
    isAuditor,
    getAirRoles,
    isReadOnly,
    canAccessEntity,
    isAuditorOfAudit,
  } = useMemo(() => {
    const activeAudits = audits?.filter((audit) => audit.isActive) || [];

    const findQuestion = (qOrId) => {
      if (!qOrId) {
        // console.warn("findQuestion() qOrId is undefined");
        return null;
      }
      const question = typeof qOrId === "string" ? questions?.find((q) => q.id === qOrId) : qOrId;
      if (!question) {
        console.log("findQuestion() question not found from syncd round questions", qOrId);
        return null;
      }
      return question;
    };

    return {
      /** @type {boolean} Hm: but the other isX here are functions -- should this be a function for consistency?
       * Reply(Ye Lynn): I agree. it should be a function for consistency.
       * */
      isOrgAdmin: checkIsOrgAdmin(userPermissions),

      /** special ESG staff role -- matches with some super-access rules in the backend
       * @type {boolean}
       */
      isESGAdmin: userPermissions?.includes("ESG_ADMIN"),

      isController: (round = selectedRound) => checkIsController(round, user, userPermissions),

      /** Controller can do whatever an owner can do */
      isOwner: (qOrId) => {
        const question = findQuestion(qOrId);
        return (
          (checkIsController(selectedRound, user, userPermissions) || checkIsOwner(question, user)) && !isReadOnly()
        );
      },

      /** Owner can do whatever an assignee can do */
      isAssigned: (qOrId) => {
        const question = findQuestion(qOrId);
        return (
          (checkIsController(selectedRound, user, userPermissions) ||
            checkIsOwner(question, user) ||
            checkIsAssigned(question, user)) &&
          !isReadOnly()
        );
      },

      isAuditor: (qOrId) => {
        const question = findQuestion(qOrId);
        return checkIsAuditor(question, activeAudits, user);
      },

      isAuditorOfAudit: (auditId) => {
        return activeAudits?.some((audit) => audit.id === auditId && includesIgnoreCase(audit?.auditors, user?.email));
      },

      // if the selected round is closed, then non of the user cannot edit anything
      isReadOnly: () => {
        return selectedRound?.state === "CLOSED";
      },

      /** true for us developers (use-case: extra controls, and debugging) */
      isDeveloper: () => {
        return userPermissions?.includes("DEV");
      },

      /** Check if user can access any-one-of the selected round's entities */
      canAccessEntity: () => {
        const roundEntities = selectedRound?.entities;
        const userEntity = userEntities?.find((userEntity) => roundEntities?.includes(userEntity.id));
        if (userEntity) {
          return true;
        }
        return checkIsOrgAdmin(userPermissions) || checkIsController(selectedRound, user, userPermissions);
      },

      /** What are the roles in this context?
       * Use-cases: for debugging, and can be used to help stuck users (e.g. "you can't do this" is a dead-end barrier, whereas "contact Alice who is the controller" is helpful). */
      getAirRoles: (args) => {
        const { question, audit } = args || {};
        const roles = {};
        roles.iam = user?.email;
        roles.isOrgAdmin = checkIsOrgAdmin(userPermissions);
        roles.selectedRound = selectedRound?.id;
        if (selectedRound) {
          roles.controllers = selectedRound.ownerEmails || "unset?!";
        }
        roles.isController = checkIsController(selectedRound, user, userPermissions);
        roles.question = question;
        roles.owner = question?.owner;
        roles.isOwner = question && checkIsOwner(question, user);
        roles.assignees = question?.assignees;
        roles.isAssignee = question && checkIsAssigned(question, user);
        roles.audit = audit;
        roles.auditors = audit?.auditors;
        roles.isAuditor = audit && checkIsAuditor(question, audit, user);
        return roles;
      },
    };
  }, [audits, userPermissions, questions, selectedRound, user, userEntities]); // ./useAirUserRoles

  return {
    isOrgAdmin,
    isESGAdmin,
    isController,
    isDeveloper,
    isOwner,
    isAssigned,
    isAuditor,
    isReadOnly,
    getAirRoles,
    canAccessEntity,
    isAuditorOfAudit,
  };
};

/**
 * Uses: match-any if multiple values are provided (which is unusual)
 * @param {*} userRoles
 * @param {*} pagePermissions
 * @param {*} pageModules Usually unset. Allows for menu & sub-menu modules
 * @returns
 */
export function isAllowedToView(userRoles, pagePermissions, pageModules) {
  // page permissions - match any
  if (pagePermissions && pagePermissions?.length > 0) {
    const ok = pagePermissions.some((element) => {
      return userRoles?.includes(element);
    });
    if (!ok) {
      return false;
    }
  }
  // page modules - match any
  if (pageModules && pageModules?.length > 0) {
    const ok = pageModules.some(isModuleAvailable);
    if (!ok) {
      return false;
    }
  }
  // OK!
  return true;
}

/**
 * If no module is specified, allow it. if specified, check if the module is in the list of available modules
 */
export function isModuleAvailable(module) {
  if (!module || GLOBAL.organizationModules?.includes(module)) {
    return true;
  }
  assert(typeof module === "string", "module must be a string", module);
  return false;
}

export default useAirUserRoles;
